import React from 'react';
import { Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const FrozenInvitation = ({ invitation, showWishlistButton = false, eventId, onRSVPClick }) => {
  const navigate = useNavigate();

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const hasCustomBackground = invitation.background_image_url && invitation.background_image_url.trim() !== '';
  
  return (
    <div className="relative overflow-hidden" style={{
      background: hasCustomBackground 
        ? `url(${invitation.background_image_url}) center/cover no-repeat`
        : 'linear-gradient(180deg, #4A90E2 0%, #5B9DD9 40%, #6CADD0 100%)',
      minHeight: '100vh'
    }}>
      {/* Dark overlay for custom backgrounds to ensure text readability */}
      {hasCustomBackground && (
        <div className="absolute inset-0 bg-black/40 z-0"></div>
      )}
      
      {/* Hanging Snowflakes at Top */}
      <div className="absolute top-0 left-0 right-0 h-32 overflow-hidden pointer-events-none z-0">
        {[...Array(15)].map((_, i) => (
          <div
            key={`hanging-${i}`}
            className="absolute"
            style={{
              left: `${(i * 100) / 15}%`,
              top: `-${Math.random() * 20}px`,
              animation: `swing ${3 + Math.random() * 2}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          >
            <div className="text-white text-3xl opacity-80">❄️</div>
          </div>
        ))}
      </div>
      
      {/* Floating Snowflakes Animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute text-2xl opacity-60 animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 5}s`
            }}
          >
            ❄️
          </div>
        ))}
      </div>

      <div className="relative z-10 max-w-4xl mx-auto p-4 sm:p-8">
        {/* Main Invitation Card */}
        <div className="backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden" style={{
          background: 'linear-gradient(180deg, rgba(74, 144, 226, 0.95) 0%, rgba(91, 157, 217, 0.95) 40%, rgba(108, 173, 208, 0.95) 100%)'
        }}>
          
          {/* Frozen-themed Header */}
          <div className="relative p-8 text-center">
            {/* Frozen Quote */}
            <div className="mb-6">
              <p className="text-white text-lg sm:text-xl font-light tracking-widest uppercase mb-2" style={{textShadow: '1px 1px 2px rgba(0,0,0,0.3)', letterSpacing: '0.2em'}}>
                For the first time in forever
              </p>
              <p className="text-white text-xl sm:text-2xl font-medium uppercase" style={{textShadow: '1px 1px 2px rgba(0,0,0,0.3)', letterSpacing: '0.15em'}}>
                Join us in celebrating
              </p>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-4" style={{textShadow: '3px 3px 6px rgba(0,0,0,0.4)', fontFamily: 'Georgia, serif'}}>
              {invitation.person_names}'s
            </h1>
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="text-4xl sm:text-5xl font-bold text-white" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
                {invitation.event_title.includes('Birthday') ? invitation.event_title.match(/\d+/)?.[0] || '' : ''}
              </span>
              {invitation.event_title.includes('Birthday') && (
                <span className="text-2xl sm:text-3xl font-medium text-white align-top" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
                  {invitation.event_title.match(/\d+/)?.[0] ? (() => {
                    const num = parseInt(invitation.event_title.match(/\d+/)[0]);
                    if (num === 1) return 'st';
                    if (num === 2) return 'nd';
                    if (num === 3) return 'rd';
                    return 'th';
                  })() : ''}
                </span>
              )}
            </div>
            <p className="text-3xl sm:text-4xl font-medium text-white uppercase tracking-wider" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)', letterSpacing: '0.1em'}}>
              {invitation.event_type}
            </p>
          </div>

          {/* Photo Section */}
          {invitation.photo_url && (
            <div className="flex justify-center p-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full blur-xl opacity-50"></div>
                <img
                  src={invitation.photo_url}
                  alt="Birthday star"
                  className="relative w-48 h-48 rounded-full object-cover border-8 border-white shadow-xl"
                />
              </div>
            </div>
          )}

          {/* Event Details */}
          <div className="p-8 space-y-6">
            {/* Date & Time - Professional Style */}
            <div className="text-center mb-6">
              <p className="text-white text-xl sm:text-2xl font-medium uppercase tracking-wider mb-2" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
                {formatDate(invitation.date_from)}
              </p>
              {invitation.date_to && (
                <p className="text-white text-lg opacity-90" style={{textShadow: '1px 1px 2px rgba(0,0,0,0.3)'}}>
                  to {formatDate(invitation.date_to)}
                </p>
              )}
              <p className="text-white text-xl sm:text-2xl font-medium mt-2" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
                at {formatTime(invitation.time_from)}
              </p>
              {invitation.time_to && (
                <p className="text-white text-lg opacity-90" style={{textShadow: '1px 1px 2px rgba(0,0,0,0.3)'}}>
                  to {formatTime(invitation.time_to)}
                </p>
              )}
            </div>

            {/* Venue - Professional Style */}
            <div className="text-center mb-6">
              <p className="text-white text-lg sm:text-xl whitespace-pre-line leading-relaxed" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
                {invitation.venue}
              </p>
            </div>

            {/* Organizers/RSVP */}
            {invitation.organizers && (
              <div className="text-center">
                <p className="text-white text-lg" style={{textShadow: '1px 1px 2px rgba(0,0,0,0.3)'}}>
                  <span className="font-medium">RSVP to {invitation.organizers}</span>
                </p>
              </div>
            )}

            {/* RSVP Section */}
            {onRSVPClick && (
              <div className="text-center p-6 bg-white/20 backdrop-blur rounded-2xl mb-6">
                <p className="text-white text-xl font-semibold mb-4" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
                  Will you join us? 🎉
                </p>
                <Button
                  onClick={onRSVPClick}
                  size="lg"
                  className="gap-2 px-8 py-6 text-lg font-bold w-full sm:w-auto"
                  style={{background: 'linear-gradient(135deg, #a855f7 0%, #3b82f6 100%)'}}
                >
                  ✉️ RSVP Now
                </Button>
              </div>
            )}

            {/* Wishlist Link */}
            {showWishlistButton && (
              <div className="text-center p-6 bg-gradient-to-r from-purple-100 to-blue-100 rounded-2xl">
                <p className="text-lg mb-4" style={{color: '#6366f1'}}>
                  <strong>✨ {invitation.person_names}</strong> has shared a special gift wishlist!
                  <br />
                  <span className="text-base text-gray-600">Take a peek and pick something magical if you'd like 🎁</span>
                </p>
                <Button
                  onClick={() => navigate(`/e/${eventId}`)}
                  size="lg"
                  className="gap-2 w-full sm:w-auto"
                  style={{background: 'linear-gradient(135deg, #a855f7 0%, #3b82f6 100%)'}}
                >
                  <Gift className="w-5 h-5" />
                  View Wishlist
                </Button>
              </div>
            )}
          </div>

        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        @keyframes swing {
          0%, 100% { transform: rotate(-5deg); }
          50% { transform: rotate(5deg); }
        }
      `}</style>
    </div>
  );
};

export default FrozenInvitation;